import { useState, type FormEvent } from "react";
import { useAction, useConvex, useMutation, usePaginatedQuery, useQuery } from "convex/react";
import { AlertTriangle, ArrowRight, Check, Copy, Download, Eye, Layers, Pencil, RefreshCw, Square } from "lucide-react";
import { api } from "../../convex/_generated/api";
import { WlsLogo } from "../components/WlsLogo";
import type { Doc, Id } from "../../convex/_generated/dataModel";
import { Badge, Button, Card, ConfirmDialog, EmptyState, Field, Input, Modal, Notice, PageHeader, TableSkeleton } from "../components/ui";
import { CATEGORY_LABELS, errorMessage, formatDate, signalLabel } from "../lib/format";
import { canonicalPrivateWlsTeamsUrl, parsePrivateWlsTeamsUrl } from "../lib/privateWlsUrl";
import { banReason, copyText, downloadDiscordIds, type BanCategory } from "../lib/banTools";
import { BanReasonPicker } from "../components/BanReasonPicker";
import { describeEvasionProof } from "../lib/evasionProof";

type AlertSanction = {
  id: string; category: BanCategory; reason?: string; serversBan?: string;
  status: "active" | "lifted"; addedAt: number;
  epicId?: string; epicPseudo?: string; discordId?: string; discordPseudo?: string;
  evasion: boolean; createdHere: boolean;
};
type Alert = Doc<"watchAlerts"> & { sanctions: AlertSanction[]; category?: BanCategory };
/** La liste ne renvoie pas les clés déjà alertées : elles restent côté serveur. */
type Watch = Omit<Doc<"tournamentWatches">, "alertedKeys" | "ingestTokenDigest">;

function remaining(expiresAt: number) {
  const left = expiresAt - Date.now();
  if (left <= 0) return "terminée";
  const hours = Math.floor(left / 3_600_000);
  const minutes = Math.floor((left % 3_600_000) / 60_000);
  return hours > 0 ? `${hours} h ${minutes} min restantes` : `${minutes} min restantes`;
}

/** Valeur d'un champ `datetime-local`, à l'heure locale du navigateur. */
function toLocalInput(value: number) {
  const date = new Date(value);
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Proposition par défaut : la prochaine heure pleine, pour trois heures. */
function defaultWindow() {
  const start = new Date();
  start.setHours(start.getHours() + 1, 0, 0, 0);
  return { start: toLocalInput(start.getTime()), end: toLocalInput(start.getTime() + 3 * 3_600_000) };
}

type DmCandidate = { _id: Id<"users">; name: string; discordId: string; isOwner: boolean; avatarUrl: string | null };

/** Choix des administrateurs prévenus en DM Discord pour une surveillance. */
function DmPicker({ candidates, value, onChange }: {
  candidates: DmCandidate[] | undefined; value: Id<"users">[]; onChange: (next: Id<"users">[]) => void;
}) {
  return (
    <fieldset className="dm-picker">
      <legend>Prévenir en DM Discord</legend>
      {candidates === undefined ? <span className="muted">Chargement…</span> : candidates.length === 0 ? (
        <span className="muted">Aucun administrateur avec un ID Discord.</span>
      ) : candidates.map((user) => {
        const checked = value.includes(user._id);
        return (
          <label key={user._id} className={checked ? "dm-chip active" : "dm-chip"} title={user.discordId}>
            <input type="checkbox" checked={checked}
              onChange={(event) => onChange(event.target.checked ? [...value, user._id] : value.filter((id) => id !== user._id))} />
            <span className="dm-chip__avatar" aria-hidden="true">
              {user.avatarUrl ? <img src={user.avatarUrl} alt="" /> : user.name.slice(0, 1).toUpperCase()}
            </span>
            {user.name}
            {checked && <Check size={13} aria-hidden="true" />}
          </label>
        );
      })}
    </fieldset>
  );
}

/** Nom et horaires d'une surveillance : début et fin tant qu'elle est programmée, la fin seule ensuite. */
function EditWatchModal({ watch, candidates, onClose }: { watch: Watch; candidates: DmCandidate[] | undefined; onClose: () => void }) {
  const rename = useMutation(api.watches.rename);
  const reschedule = useMutation(api.watches.reschedule);
  const setDmRecipients = useMutation(api.watches.setDmRecipients);
  const [dmUserIds, setDmUserIds] = useState<Id<"users">[]>(watch.dmUserIds ?? []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [label, setLabel] = useState(watch.label || watch.tournamentId);
  const [start, setStart] = useState(toLocalInput(watch.startedAt));
  const [end, setEnd] = useState(toLocalInput(watch.expiresAt));
  const scheduled = watch.status === "scheduled";

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true); setError("");
    try {
      if (label.trim() !== (watch.label || watch.tournamentId)) await rename({ watchId: watch._id, label });
      const startsAt = new Date(start).getTime();
      const endsAt = new Date(end).getTime();
      const startChanged = scheduled && start !== toLocalInput(watch.startedAt);
      if (startChanged || end !== toLocalInput(watch.expiresAt)) {
        await reschedule({ watchId: watch._id, endsAt, ...(scheduled ? { startsAt } : {}) });
      }
      const initial = watch.dmUserIds ?? [];
      const sameRecipients = dmUserIds.length === initial.length && dmUserIds.every((id) => initial.includes(id));
      if (!sameRecipients) await setDmRecipients({ watchId: watch._id, userIds: dmUserIds });
      onClose();
    } catch (reason) { setError(errorMessage(reason)); setLoading(false); }
  };

  return (
    <Modal title="Modifier la surveillance"
      description={scheduled ? "Le début et la fin restent modifiables jusqu’au démarrage." : "La surveillance a démarré : sa fin et les admins prévenus peuvent encore changer."}
      onClose={onClose}>
      <form onSubmit={submit} className="form-stack">
        <Field label="Nom de la surveillance">
          <Input minLength={2} maxLength={80} required value={label} onChange={(event) => setLabel(event.target.value)} autoFocus />
        </Field>
        <div className="form-grid">
          <Field label="Début">
            <Input type="datetime-local" required value={start} disabled={!scheduled}
              onChange={(event) => setStart(event.target.value)} />
          </Field>
          <Field label="Fin">
            <Input type="datetime-local" required value={end} min={scheduled ? start : toLocalInput(Date.now())}
              onChange={(event) => setEnd(event.target.value)} />
          </Field>
        </div>
        <DmPicker candidates={candidates} value={dmUserIds} onChange={setDmUserIds} />
        {error && <Notice tone="error">{error}</Notice>}
        <div className="modal-actions">
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button type="submit" loading={loading}>Enregistrer</Button>
        </div>
      </form>
    </Modal>
  );
}

function EvasionProofBlock({ evidence, compact = false }: { evidence: Alert["evidence"]; compact?: boolean }) {
  const proof = describeEvasionProof(evidence);
  if (!proof) return null;
  return (
    <div className={`evasion-proof${compact ? " evasion-proof--compact" : ""}`}>
      <div className="evasion-proof__line">
        <span>{proof.partageLabel}</span>
        <code>{proof.partageValeur || "—"}</code>
      </div>
      <div className="evasion-proof__line evasion-proof__line--change">
        <span>{proof.changeLabel}</span>
        <code>{proof.anciens.join(" / ")}</code>
        <ArrowRight size={13} aria-hidden="true" />
        <code>{proof.nouveau}</code>
      </div>
    </div>
  );
}

function AlertProfileModal({ alert, onClose }: { alert: Alert; onClose: () => void }) {
  const name = alert.registrant.epicName ?? alert.registrant.wlsName ?? alert.registrant.epicId ?? "Joueur";
  return (
    <Modal title={name} description={`Détecté le ${formatDate(alert.createdAt)} dans le tournoi ${alert.tournamentId}.`} className="modal--wide" onClose={onClose}>
      <div className="alert-profile-grid">
        <section className="alert-profile-card">
          <h3>Profil détecté</h3>
          <dl className="alert-profile-facts">
            <dt>Pseudo Epic</dt><dd>{alert.registrant.epicName || "—"}</dd>
            <dt>Epic ID</dt><dd className="mono-wrap">{alert.registrant.epicId || "—"}</dd>
            <dt>Pseudo Discord</dt><dd>{alert.registrant.discordName || "—"}</dd>
            <dt>Discord ID</dt><dd className="mono-wrap">{alert.registrant.discordId || "—"}</dd>
            <dt>Compte WLS</dt><dd>{alert.registrant.wlsName || "—"}</dd>
            <dt>Équipe</dt><dd>{alert.registrant.owner || "—"}</dd>
          </dl>
        </section>
        <section className="alert-profile-card">
          <h3>Détection</h3>
          <div className="profile-badges">
            {alert.banEvasion && <Badge tone="evasion"><Layers size={11} /> Contournement</Badge>}
            <Badge tone={alert.level === "high" ? "danger" : alert.level === "medium" ? "warning" : "neutral"}>{alert.score}%</Badge>
          </div>
          {alert.banEvasion && <EvasionProofBlock evidence={alert.evidence} />}
          <details className="alert-evidence-disclosure" open={!alert.banEvasion}>
            <summary>Tous les signaux techniques ({alert.evidence.length})</summary>
            <div className="alert-evidence-details">
              {alert.evidence.map((item, index) => (
                <div key={`${item.blacklistId}-${index}`}>
                  <strong>{signalLabel(item.signal)} · {item.confidence}%</strong>
                  <small>Détecté : <code>{item.matchedValue}</code></small>
                  <small>Blacklist : <code>{item.blacklistValue}</code></small>
                </div>
              ))}
            </div>
          </details>
        </section>
      </div>
      <section className="alert-sanctions">
        <h3>Fiches blacklist associées</h3>
        {alert.sanctions.length === 0 && <p className="muted">Aucune fiche blacklist associée n’est encore disponible.</p>}
        {alert.sanctions.map((sanction) => (
          <article key={sanction.id} className={sanction.createdHere ? "alert-sanction alert-sanction--new" : "alert-sanction"}>
            <div><strong>{sanction.epicPseudo || sanction.discordPseudo || "Compte blacklisté"}</strong><small>{formatDate(sanction.addedAt)}</small></div>
            <Badge tone={sanction.evasion ? "evasion" : "danger"}>{CATEGORY_LABELS[sanction.category]}</Badge>
            <p>{sanction.reason || "Sans motif renseigné"}</p>
            <small>Epic {sanction.epicId || "—"} · Discord {sanction.discordId || "—"}</small>
            {sanction.serversBan && <small>Serveurs : {sanction.serversBan}</small>}
          </article>
        ))}
      </section>
    </Modal>
  );
}

export function WatchPage() {
  const convex = useConvex();
  const me = useQuery(api.users.me);
  const watches = useQuery(api.watches.list);
  const start = useMutation(api.watches.start);
  const startPrivate = useAction(api.watches.startPrivate);
  const stop = useMutation(api.watches.stop);
  const runNow = useAction(api.watches.runNow);
  const resolve = useMutation(api.watches.resolveAlert);
  const [status, setStatus] = useState<"pending" | "handled" | "dismissed">("pending");
  const { results: alerts, status: paginationStatus, loadMore } = usePaginatedQuery(api.watches.listAlerts, { status }, { initialNumItems: 20 });
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [starting, setStarting] = useState(false);
  const [busy, setBusy] = useState<Id<"tournamentWatches"> | null>(null);
  const [exporting, setExporting] = useState<Id<"tournamentWatches"> | null>(null);
  const [profile, setProfile] = useState<Alert | null>(null);
  const [renaming, setRenaming] = useState<Watch | null>(null);
  const [confirmStop, setConfirmStop] = useState<Watch | null>(null);
  const [source, setSource] = useState<"private" | "public">("private");
  const [timing, setTiming] = useState<"now" | "scheduled">("now");
  const [dmUserIds, setDmUserIds] = useState<Id<"users">[]>([]);
  const [planned, setPlanned] = useState(defaultWindow);
  const admin = me?.role === "admin";
  const dmCandidates = useQuery(api.settings.alertDmCandidates, admin ? {} : "skip");
  const dmNames = (ids: Id<"users">[]) => ids.map((id) => dmCandidates?.find((user) => user._id === id)?.name).filter(Boolean).join(", ");

  const launch = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setStarting(true); setError("");
    try {
      const label = String(form.get("label") || "") || undefined;
      const schedule: { durationHours?: number; startsAt?: number; endsAt?: number } = timing === "scheduled"
        ? { startsAt: new Date(planned.start).getTime(), endsAt: new Date(planned.end).getTime() }
        : { durationHours: Number(form.get("hours")) || 3 };
      if (source === "private") {
        const ids = parsePrivateWlsTeamsUrl(String(form.get("privateUrl") || ""));
        if (!ids) {
          setError("Collez l’URL complète accounts.wls.gg qui se termine par /teams.");
          return;
        }
        await startPrivate({ ...ids, label, dmUserIds, ...schedule });
      } else {
        await start({
          tournamentId: String(form.get("tournamentId")).trim(), label, dmUserIds, ...schedule,
        });
      }
      formElement.reset();
      setDmUserIds([]);
      if (schedule.startsAt !== undefined && schedule.startsAt > Date.now() + 60_000) {
        setMessage(`Surveillance programmée du ${formatDate(schedule.startsAt)} au ${formatDate(schedule.endsAt)}.`);
        setPlanned(defaultWindow());
      }
    } catch (reason) { setError(errorMessage(reason)); }
    finally { setStarting(false); }
  };

  const stopWatch = async (watch: Watch) => {
    await stop({ watchId: watch._id });
  };

  const check = async (watchId: Id<"tournamentWatches">) => {
    setBusy(watchId); setError("");
    try { await runNow({ watchId }); }
    catch (reason) { setError(errorMessage(reason)); }
    finally { setBusy(null); }
  };

  const dismiss = async (alert: Alert) => {
    setError(""); setMessage("");
    try { await resolve({ alertId: alert._id, decision: "dismiss" }); }
    catch (reason) { setError(errorMessage(reason)); }
  };

  const markHandled = async (alert: Alert) => {
    setError(""); setMessage("");
    try {
      await resolve({ alertId: alert._id, decision: "handled" });
      setMessage("Alerte marquée comme traitée.");
    } catch (reason) { setError(errorMessage(reason)); }
  };

  const copyAlertValue = async (value: string, confirmation: string) => {
    setError(""); setMessage("");
    try { await copyText(value); setMessage(confirmation); }
    catch { setError("La valeur n’a pas pu être copiée."); }
  };

  const exportWatch = async (watch: Watch) => {
    setExporting(watch._id); setError(""); setMessage("");
    try {
      const exported = await convex.query(api.watches.exportDiscordIds, { watchId: watch._id });
      const count = downloadDiscordIds(exported.label, exported.ids);
      if (count === 0) setError("Aucun ID Discord exploitable dans cette surveillance.");
      else setMessage(`${count} ID Discord exporté${count > 1 ? "s" : ""}.`);
    } catch (reason) { setError(errorMessage(reason)); }
    finally { setExporting(null); }
  };

  return <>
    <PageHeader
      title="Tournois surveillés"
      description="Les groupes WLS privés sont contrôlés toutes les minutes par le serveur. Chaque joueur blacklisté qui s’inscrit déclenche une alerte."
    />
    {error && <Notice tone="error">{error}</Notice>}
    {message && <Notice tone="success">{message}</Notice>}

    {admin && (
      <Card className="check-launcher">
        <div className="card-heading"><h2>Suivre un tournoi WLS</h2></div>
        <div className="watch-source" role="group" aria-label="Source WLS">
          <button type="button" className={source === "private" ? "active" : ""} aria-pressed={source === "private"} onClick={() => setSource("private")}>Inscrits privés · 1{" "}min</button>
          <button type="button" className={source === "public" ? "active" : ""} aria-pressed={source === "public"} onClick={() => setSource("public")}>Classement public · 5{" "}min</button>
        </div>
        <div className="watch-source watch-timing" role="group" aria-label="Démarrage">
          <button type="button" className={timing === "now" ? "active" : ""} aria-pressed={timing === "now"} onClick={() => setTiming("now")}>Démarrer maintenant</button>
          <button type="button" className={timing === "scheduled" ? "active" : ""} aria-pressed={timing === "scheduled"} onClick={() => setTiming("scheduled")}>Programmer début et fin</button>
        </div>
        <form onSubmit={launch} className={`inline-form inline-form--watch${timing === "scheduled" ? " inline-form--scheduled" : ""}`}>
          {/* Sans indice sous ce champ, tous les libellés partagent la même hauteur et la
              ligne reste alignée : le format attendu est porté par le texte d'exemple. */}
          {source === "private" ? (
            <Field label="URL privée des équipes WLS">
              <Input name="privateUrl" required placeholder="https://accounts.wls.gg/api/v5/events/…/groups/…/teams" type="url"
                // Une URL copiée depuis www.wls.gg devient aussitôt son équivalent accounts.wls.gg.
                onChange={(event) => {
                  const canonical = canonicalPrivateWlsTeamsUrl(event.target.value);
                  if (canonical && canonical !== event.target.value) event.target.value = canonical;
                }} />
            </Field>
          ) : (
            <Field label="Identifiant du classement WLS">
              <Input name="tournamentId" required placeholder="680701783202336768" inputMode="numeric" />
            </Field>
          )}
          <Field label="Nom (facultatif)"><Input name="label" placeholder="War Legend Cup" /></Field>
          {timing === "now" ? (
            <Field label="Durée">
              <select name="hours" className="input" defaultValue="3">
                <option value="1">1 heure</option>
                <option value="3">3 heures</option>
                <option value="6">6 heures</option>
                <option value="12">12 heures</option>
                <option value="24">24 heures</option>
                <option value="72">3 jours</option>
                <option value="168">7 jours</option>
              </select>
            </Field>
          ) : <>
            <Field label="Début">
              <Input type="datetime-local" required value={planned.start} min={toLocalInput(Date.now())}
                onChange={(event) => setPlanned((current) => ({ ...current, start: event.target.value }))} />
            </Field>
            <Field label="Fin">
              <Input type="datetime-local" required value={planned.end} min={planned.start}
                onChange={(event) => setPlanned((current) => ({ ...current, end: event.target.value }))} />
            </Field>
          </>}
          <Button type="submit" loading={starting}>{timing === "scheduled" ? "Programmer" : "Surveiller"}</Button>
        </form>
        <DmPicker candidates={dmCandidates} value={dmUserIds} onChange={setDmUserIds} />
      </Card>
    )}

    <div className="section-heading"><h2>Surveillances</h2></div>
    {watches === undefined ? <TableSkeleton /> : watches.length === 0 ? (
      <EmptyState title="Aucune surveillance" description="Lancez une surveillance pour être alerté dès qu’un joueur blacklisté s’inscrit." />
    ) : (
      <Card className="check-list">
        {watches.map((watch) => (
          <div key={watch._id} className="check-row">
            <span className="check-row__source" title="Tournoi WLS surveillé"><WlsLogo size={20} /></span>
            <div className="check-row__main">
              <strong>{watch.label || watch.tournamentId}</strong>
              <small>
                {watch.source === "private_server" || watch.source === "private_browser"
                  ? `Événement ${watch.eventId} · groupe ${watch.groupId} · toutes les minutes · `
                  : watch.label ? `${watch.tournamentId} · toutes les cinq minutes · ` : "Toutes les cinq minutes · "}
                {watch.status === "scheduled"
                  ? `débute le ${formatDate(watch.startedAt)} · se termine le ${formatDate(watch.expiresAt)}`
                  : watch.status === "active" ? `${remaining(watch.expiresAt)} · fin le ${formatDate(watch.expiresAt)}` : `terminée le ${formatDate(watch.expiresAt)}`}
                {watch.status === "scheduled" ? "" : watch.lastCheckedAt ? ` · dernier contrôle ${formatDate(watch.lastCheckedAt)}` : " · en attente du premier contrôle"}
              </small>
              {admin && <small>{watch.dmUserIds?.length ? `DM : ${dmNames(watch.dmUserIds) || `${watch.dmUserIds.length} admin(s)`}` : "Aucun DM Discord"}</small>}
              {watch.lastError && <small className="check-row__error">{watch.lastError}</small>}
            </div>
            <div className="check-row__metrics"><strong>{watch.registrantsSeen}</strong><small>inscrits</small></div>
            <div className="check-row__metrics"><strong>{watch.alerts}</strong><small>alertes</small></div>
            <Badge tone={watch.status === "active" ? "success" : watch.status === "scheduled" ? "warning" : "neutral"}>
              {watch.status === "active" ? "Active" : watch.status === "scheduled" ? "Programmée" : watch.status === "stopped" ? "Arrêtée" : "Terminée"}
            </Badge>
            <div className="row-actions">
              <button type="button" className="icon-button" title="Exporter les IDs Discord" aria-label={`Exporter les IDs Discord de ${watch.label || watch.tournamentId}`} disabled={exporting === watch._id} onClick={() => void exportWatch(watch)}>
                <Download size={15} className={exporting === watch._id ? "spin" : undefined} />
              </button>
              {admin && <>
                <button type="button" className="icon-button" title="Modifier le nom et les horaires" aria-label={`Modifier ${watch.label || watch.tournamentId}`} onClick={() => setRenaming(watch)}>
                  <Pencil size={15} />
                </button>
                {watch.status === "active" && (
                  <button type="button" className="icon-button" title="Contrôler maintenant" aria-label="Contrôler maintenant" disabled={busy === watch._id} onClick={() => void check(watch._id)}>
                    <RefreshCw size={15} className={busy === watch._id ? "spin" : undefined} />
                  </button>
                )}
                <button type="button" className="icon-button icon-button--danger"
                  title={watch.status === "scheduled" ? "Annuler la programmation" : "Arrêter la surveillance"}
                  aria-label={watch.status === "scheduled" ? "Annuler la programmation" : "Arrêter la surveillance"} onClick={() => setConfirmStop(watch)}>
                  <Square size={15} />
                </button>
              </>}
            </div>
          </div>
        ))}
      </Card>
    )}

    <div className="section-heading">
      <h2>Joueurs détectés</h2>
      <div className="section-actions">
        <BanReasonPicker />
        <div className="segmented" role="group" aria-label="Statut des alertes">
          <button type="button" aria-pressed={status === "pending"} className={status === "pending" ? "active" : ""} onClick={() => setStatus("pending")}>À traiter</button>
          <button type="button" aria-pressed={status === "handled"} className={status === "handled" ? "active" : ""} onClick={() => setStatus("handled")}>Traitées</button>
          <button type="button" aria-pressed={status === "dismissed"} className={status === "dismissed" ? "active" : ""} onClick={() => setStatus("dismissed")}>Écartées</button>
        </div>
      </div>
    </div>
    {paginationStatus === "LoadingFirstPage" ? <TableSkeleton /> : alerts.length === 0 ? (
      <EmptyState
        title={status === "pending" ? "Aucune alerte en attente" : "Aucune alerte"}
        description={status === "pending" ? "Aucun joueur blacklisté n’a été repéré dans les tournois surveillés." : "Rien à afficher pour ce filtre."}
      />
    ) : (
      <Card className="check-list">
        {(alerts as Alert[]).map((alert) => (
          <div key={alert._id} className="alert-row">
            <span className={`alert-row__level alert-row__level--${alert.level}`}><AlertTriangle size={15} /></span>
            <div className="alert-row__main">
              <strong>{alert.registrant.epicName || alert.registrant.wlsName || alert.registrant.epicId || "Joueur inconnu"}</strong>
              <small>Epic {alert.registrant.epicId || "—"} · Discord {alert.registrant.discordId || "—"}</small>
              <small>Tournoi {alert.tournamentId} · {formatDate(alert.createdAt)}</small>
              {alert.banEvasion ? <EvasionProofBlock evidence={alert.evidence} compact /> : (
                <div className="evidence-list">
                  {alert.evidence.slice(0, 3).map((item, index) => (
                    <span key={`${item.blacklistId}-${index}`}>{signalLabel(item.signal)} · {item.confidence}%</span>
                  ))}
                  {alert.evidence.length > 3 && <span className="evidence-more">+{alert.evidence.length - 3} autres signaux</span>}
                </div>
              )}
            </div>
            <div className="alert-row__side">
              {alert.banEvasion && <Badge tone="evasion" title="Le joueur revient sous un autre compte"><Layers size={11} /> Contournement</Badge>}
              <Badge tone={alert.level === "high" ? "danger" : alert.level === "medium" ? "warning" : "neutral"}>{alert.score}%</Badge>
            </div>
            <div className="alert-row__actions">
              <Button variant="secondary" onClick={() => setProfile(alert)}><Eye size={14} /> Détails</Button>
              {alert.registrant.discordId && (
                <Button variant="secondary" onClick={() => void copyAlertValue(alert.registrant.discordId!, "ID Discord copié.")}><Copy size={14} /> ID</Button>
              )}
              <Button variant="secondary" onClick={() => void copyAlertValue(banReason(), "Motif copié.")}><Copy size={14} /> Motif</Button>
              {admin && alert.status === "pending" && <>
                <Button variant="secondary" onClick={() => void dismiss(alert)}>Écarter</Button>
                <Button onClick={() => void markHandled(alert)}><Check size={14} /> Traité</Button>
              </>}
            </div>
          </div>
        ))}
        {paginationStatus === "CanLoadMore" && <div className="load-more"><Button variant="secondary" onClick={() => loadMore(20)}>Afficher plus</Button></div>}
      </Card>
    )}

    {profile && <AlertProfileModal alert={profile} onClose={() => setProfile(null)} />}
    {renaming && <EditWatchModal watch={renaming} candidates={dmCandidates} onClose={() => setRenaming(null)} />}
    {confirmStop && (
      <ConfirmDialog
        title={confirmStop.status === "scheduled" ? "Annuler la programmation" : "Arrêter la surveillance"}
        description={confirmStop.status === "scheduled"
          ? "La surveillance ne démarrera pas à l’heure prévue."
          : "Le tournoi ne sera plus contrôlé automatiquement. Les alertes déjà émises restent disponibles."}
        target={confirmStop.label || confirmStop.tournamentId}
        confirmLabel={confirmStop.status === "scheduled" ? "Annuler la programmation" : "Arrêter"}
        onConfirm={() => stopWatch(confirmStop)}
        onClose={() => setConfirmStop(null)}
      />
    )}
  </>;
}
